import * as React from 'react';
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  type Locale,
} from 'date-fns';
import { cn } from '../../utils';
import { zineZeroPad } from '../p2e/zine/zine-theme';
import { PROGRAMME_ROOT_CLASS, ProgrammeCalendarStyles } from './programme-styles';

/**
 * A single entry rendered on the Programme calendar. Times are ISO UTC
 * strings; display formatting converts to the viewer's local timezone.
 *
 * The kind system is data-driven: each event carries a stable kind key, an
 * already-translated label and a spot-ink hex. Events with the same key are
 * one legend row and share a mini-month dot color.
 */
export type ProgrammeCalendarEvent = {
  eventId: string;
  title: string;
  startTime: string;
  endTime?: string | null;
  /** Stable kind key, e.g. "class", "tutor", "cat:<categoryId>". */
  kind: string;
  /** Translated kind label (entry badge, modal kind line, legend row). */
  kindLabel: string;
  /** Bright spot-ink hex for this kind's dots (mini-month, legend dot). */
  kindInk: string;
  /**
   * AA-safe ink for this kind's small label text (entry badge, modal kind
   * line) — ≥ 4.5:1 on the paper background. Often equals `kindInk`.
   */
  kindLabelInk: string;
  hostName?: string | null;
  hostAvatarUrl?: string | null;
  /** Human-readable location (URLs stripped). Feeds the modal "Where" row. */
  locationText?: string | null;
  /** Meeting URL — enables the Join buttons (today clip + modal). */
  joinUrl?: string | null;
  /** Opaque class link; the modal's "Go to class" renders only when set. */
  classId?: string | null;
};

/** A derived "How to read" legend row (one per kind present in the month). */
export type ProgrammeLegendRow = {
  kind: string;
  label: string;
  ink: string;
};

/**
 * All user-facing strings the calendar renders. Consumers translate with
 * their own i18n namespace so the component stays namespace-agnostic.
 */
export type ProgrammeCalendarLabels = {
  /** "Week by week" eyebrow above the spread title. */
  eyebrow: string;
  /** "This month" spread title. */
  title: string;
  /** aria-label of the ‹ month button. */
  prevMonth: string;
  /** aria-label of the › month button. */
  nextMonth: string;
  /** "Today" month-bar button. */
  today: string;
  /** "Today" flag on the taped clip (CSS uppercases it). */
  todayFlag: string;
  /** "This week" prefix on the current week's label. */
  thisWeek: string;
  /** "Join now" button on the today clip (rendered per event). */
  joinNow: string;
  /** "Join" button in the modal. */
  join: string;
  /**
   * Note shown in the modal when an event has a meeting link but the join
   * window hasn't opened yet, e.g. "The join link opens 1 hour before".
   */
  joinWindowNote?: string | null;
  /** "Go to class →" button in the modal. */
  goToClass: string;
  /** aria-label of the modal's ✕ button. */
  close: string;
  /** "↑ Scroll up · N earlier weeks" sticky sign. */
  scrollUp: (weeks: number) => string;
  /** "↓ Back to today" sticky sign. */
  backToToday: string;
  /** "July at a glance" mini-month title (receives the month name). */
  atAGlance: (monthName: string) => string;
  /** "starts in 40 min" status on a today entry that is about to start. */
  startsIn: (minutes: number) => string;
  /** "happening now" status on a today entry that is running. */
  happeningNow: string;
  /** "with Teacher Marcos" sub-line under a regular entry title. */
  entrySub: (host: string) => string;
  /** "With" modal row label. */
  with: string;
  /** "Where" modal row label. */
  where: string;
  /** "How to read" legend tab. */
  legendTitle: string;
  /** "Class resources" tab over the resource-links panel (when links exist). */
  resourcesTitle?: string | null;
  /** Italic note under the legend rows. */
  legendNote?: string | null;
  /** aria-label of the month section. */
  monthSectionAria?: string;
  /** aria-label of the interlude quote section. */
  quoteAria?: string;
};

export type ProgrammeInterlude = {
  quote: string;
  attribution: string;
};

/** An external resource link rendered as a pill button in the sidebar rail. */
export type ProgrammeResourceLink = {
  label: string;
  href: string;
};

export type ProgrammeCalendarProps = {
  /** Controlled displayed month (any date within it). */
  month: Date;
  onMonthChange?: (date: Date) => void;
  events?: ProgrammeCalendarEvent[];
  labels: ProgrammeCalendarLabels;
  /** date-fns locale for month/weekday names. */
  locale?: Locale;
  /** Optional pink interlude band under the spread. */
  interlude?: ProgrammeInterlude | null;
  /**
   * Preferred order of kind keys in the derived "How to read" legend; kinds
   * present in the month but not listed here follow, in first-appearance
   * order. The legend only ever shows kinds that occur in the displayed month.
   */
  legendKindOrder?: string[];
  /** When provided, the modal shows "Go to class" for events with a classId. */
  onGoToClass?: (event: ProgrammeCalendarEvent) => void;
  /** External links (notes, recordings, …) shown as buttons under the legend. */
  resourceLinks?: ProgrammeResourceLink[];
  className?: string;
};

type DayGroup = {
  key: string;
  date: Date;
  events: ProgrammeCalendarEvent[];
};

type WeekGroup = {
  key: string;
  /** Start of the calendar week (not clipped to the month). */
  weekStart: Date;
  /** Label range: max(weekStart, monthStart) – weekEnd (as in the design). */
  labelStart: Date;
  labelEnd: Date;
  days: DayGroup[];
};

type MiniCell = {
  key: string;
  date: Date;
  inMonth: boolean;
  /** Unique kind inks of the day's events, in first-appearance order. */
  dayKinds: { key: string; ink: string }[];
  hasEvents: boolean;
};

const dateKeyOf = (date: Date) => format(date, 'yyyy-MM-dd');
const timeOf = (iso: string) => format(new Date(iso), 'HH:mm');

/** Same offset the design's script uses when anchoring the listing. */
const topOf = (el: HTMLElement) => Math.max(0, el.offsetTop - 46);

const FLASH_CLEAR_MS = 1400;
const STACKED_FLASH_DELAY_MS = 380;

/** Joining opens this long before the start and closes when the event ends. */
const JOIN_WINDOW_MS = 60 * 60_000;
/** Assumed length of an event that came without an explicit end time. */
const FALLBACK_DURATION_MS = 60 * 60_000;

/** Where an event sits relative to now: not yet, about to start, running, done. */
type EntryPhase = 'later' | 'soon' | 'live' | 'over';

function entryPhaseOf(event: ProgrammeCalendarEvent, nowMs: number): EntryPhase {
  const startMs = new Date(event.startTime).getTime();
  const endMs = event.endTime
    ? new Date(event.endTime).getTime()
    : startMs + FALLBACK_DURATION_MS;
  if (nowMs >= endMs) return 'over';
  if (nowMs >= startMs) return 'live';
  return startMs - nowMs <= JOIN_WINDOW_MS ? 'soon' : 'later';
}

/** Join is offered only inside the window — an hour ahead through the end. */
function canJoin(event: ProgrammeCalendarEvent, nowMs: number) {
  if (!event.joinUrl) return false;
  const phase = entryPhaseOf(event, nowMs);
  return phase === 'soon' || phase === 'live';
}

function openJoinUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * "Calendar D — The Programme": a zine/magazine-style month calendar.
 * Presentational and read-only — consumers supply the events, labels, locale
 * and handlers. Visuals are transposed 1:1 from the design spec (see
 * programme-styles.tsx); behaviors mirror the spec's script: the listing
 * opens anchored on today, the sticky sign toggles between "scroll up" and
 * "back to today", mini-month days jump-and-flash the listing, and entries
 * open a zine-clip modal.
 */
export function ProgrammeCalendar({
  month,
  onMonthChange,
  events = [],
  labels,
  locale,
  interlude,
  legendKindOrder,
  onGoToClass,
  resourceLinks,
  className,
}: ProgrammeCalendarProps) {
  // Minute tick so "starts in N min" and past/today states stay fresh.
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const today = startOfDay(now);
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const monthKey = format(monthStart, 'yyyy-MM');
  const monthContainsToday = isSameMonth(today, monthStart);

  // ─── Left column: the month's event days grouped into weeks ───
  const eventsByDay = React.useMemo(() => {
    const map = new Map<string, ProgrammeCalendarEvent[]>();
    for (const event of events) {
      const start = new Date(event.startTime);
      if (!isSameMonth(start, monthStart)) continue;
      const key = dateKeyOf(start);
      const list = map.get(key) ?? [];
      list.push(event);
      map.set(key, list);
    }
    map.forEach((list) =>
      list.sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      ),
    );
    return map;
  }, [events, monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const weeks = React.useMemo<WeekGroup[]>(() => {
    const dayKeys = Array.from(eventsByDay.keys()).sort();
    const byWeek = new Map<string, WeekGroup>();
    for (const key of dayKeys) {
      const date = startOfDay(new Date(`${key}T00:00:00`));
      const weekStart = startOfWeek(date, { locale });
      const weekKey = dateKeyOf(weekStart);
      let week = byWeek.get(weekKey);
      if (!week) {
        week = {
          key: weekKey,
          weekStart,
          // Range as the design labels it: clipped to the month start on the
          // left, the full calendar week end on the right ("Jul 26 – Aug 1").
          labelStart: weekStart < monthStart ? monthStart : weekStart,
          labelEnd: endOfWeek(date, { locale }),
          days: [],
        };
        byWeek.set(weekKey, week);
      }
      week.days.push({ key, date, events: eventsByDay.get(key)! });
    }
    return Array.from(byWeek.values()).sort(
      (a, b) => a.weekStart.getTime() - b.weekStart.getTime(),
    );
  }, [eventsByDay, locale, monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Anchor day: today when listed, else the first listed day from today on.
  const anchorKey = React.useMemo(() => {
    if (!monthContainsToday) return null;
    const todayKey = dateKeyOf(today);
    if (eventsByDay.has(todayKey)) return todayKey;
    const upcoming = Array.from(eventsByDay.keys())
      .sort()
      .find((key) => key > todayKey);
    return upcoming ?? null;
  }, [eventsByDay, monthContainsToday, today]);

  const anchorWeekIndex = React.useMemo(() => {
    if (!anchorKey) return -1;
    return weeks.findIndex((week) => week.days.some((day) => day.key === anchorKey));
  }, [weeks, anchorKey]);

  const earlierWeeks = anchorWeekIndex > 0 ? anchorWeekIndex : 0;
  const signVisible = anchorKey !== null && earlierWeeks > 0;

  // ─── Scroll anchoring + sticky sign (mirrors the spec's script) ───
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const dayRefs = React.useRef(new Map<string, HTMLDivElement>());
  const anchorTopRef = React.useRef(0);
  const [atToday, setAtToday] = React.useState(true);

  React.useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const anchorEl = anchorKey ? dayRefs.current.get(anchorKey) : undefined;
    const top = anchorEl ? topOf(anchorEl) : 0;
    anchorTopRef.current = top;
    sc.scrollTop = top;
    setAtToday(sc.scrollTop >= top - 60);
  }, [monthKey, weeks, anchorKey]);

  const handleScroll = React.useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    setAtToday(sc.scrollTop >= anchorTopRef.current - 60);
  }, []);

  const handleSignClick = React.useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const isAtToday = sc.scrollTop >= anchorTopRef.current - 60;
    sc.scrollTo({ top: isAtToday ? 0 : anchorTopRef.current, behavior: 'smooth' });
  }, []);

  // ─── Jump-and-flash from the mini-month ───
  const [flashKey, setFlashKey] = React.useState<string | null>(null);
  const flashTimer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  const triggerFlash = React.useCallback((key: string) => {
    // Remove-then-re-add across frames so the animation retriggers, like the
    // spec's classList.remove / reflow / add dance.
    setFlashKey(null);
    requestAnimationFrame(() => setFlashKey(key));
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashKey(null), FLASH_CLEAR_MS);
  }, []);

  const jumpToDay = React.useCallback(
    (key: string) => {
      const sc = scrollRef.current;
      const target = dayRefs.current.get(key);
      if (!sc || !target) return;
      // On phones the calendar sits ABOVE the list, so an inner scroll alone
      // happens out of view — jump the page down to the list as well.
      const stacked = window.matchMedia('(max-width: 640px)').matches;
      if (stacked) {
        sc.scrollTop = topOf(target);
        sc.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        sc.scrollTo({ top: topOf(target), behavior: 'smooth' });
      }
      // Flash once the list is actually in view (page scroll needs a beat).
      window.setTimeout(() => triggerFlash(key), stacked ? STACKED_FLASH_DELAY_MS : 0);
    },
    [triggerFlash],
  );

  // ─── Month bar ───
  const handleToday = React.useCallback(() => {
    const current = new Date();
    if (!isSameMonth(current, month)) {
      onMonthChange?.(startOfMonth(current));
      return;
    }
    scrollRef.current?.scrollTo({ top: anchorTopRef.current, behavior: 'smooth' });
  }, [month, onMonthChange]);

  // ─── Mini-month grid ───
  const miniWeekdays = React.useMemo(() => {
    const weekStart = startOfWeek(monthStart, { locale });
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      return format(day, 'EEEEE', { locale });
    });
  }, [monthKey, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const miniWeeks = React.useMemo<MiniCell[][]>(() => {
    const gridStart = startOfWeek(monthStart, { locale });
    const gridEnd = endOfWeek(monthEnd, { locale });
    const rows: MiniCell[][] = [];
    let cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      const row: MiniCell[] = [];
      for (let i = 0; i < 7; i += 1) {
        const date = new Date(cursor);
        const key = dateKeyOf(date);
        const inMonth = isSameMonth(date, monthStart);
        const dayEvents = inMonth ? eventsByDay.get(key) ?? [] : [];
        const dayKinds: { key: string; ink: string }[] = [];
        for (const event of dayEvents) {
          if (!dayKinds.some((entry) => entry.key === event.kind)) {
            dayKinds.push({ key: event.kind, ink: event.kindInk });
          }
        }
        row.push({ key, date, inMonth, dayKinds, hasEvents: dayEvents.length > 0 });
        cursor.setDate(cursor.getDate() + 1);
      }
      rows.push(row);
    }
    return rows;
  }, [eventsByDay, locale, monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Legend: the kinds actually present in the displayed month ───
  const legendRows = React.useMemo<ProgrammeLegendRow[]>(() => {
    const seen = new Map<string, ProgrammeLegendRow & { appearance: number }>();
    const dayKeys = Array.from(eventsByDay.keys()).sort();
    for (const dayKey of dayKeys) {
      for (const event of eventsByDay.get(dayKey)!) {
        if (!seen.has(event.kind)) {
          seen.set(event.kind, {
            kind: event.kind,
            label: event.kindLabel,
            ink: event.kindInk,
            appearance: seen.size,
          });
        }
      }
    }
    const order = legendKindOrder ?? [];
    return Array.from(seen.values())
      .sort((a, b) => {
        const rankA = order.indexOf(a.kind);
        const rankB = order.indexOf(b.kind);
        const keyA = rankA === -1 ? order.length + a.appearance : rankA;
        const keyB = rankB === -1 ? order.length + b.appearance : rankB;
        return keyA - keyB;
      })
      .map(({ kind, label, ink }) => ({ kind, label, ink }));
  }, [eventsByDay, legendKindOrder]);

  // ─── Modal ───
  const [selected, setSelected] = React.useState<ProgrammeCalendarEvent | null>(null);
  const modalTitleId = React.useId();
  const closeModal = React.useCallback(() => setSelected(null), []);

  React.useEffect(() => {
    if (!selected) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected]);

  // ─── Renderers ───
  /**
   * One event row. On the today clip the row also carries its own status line
   * and Join button, so a day with several events never leaves a lone
   * "Join now" whose target is ambiguous. Every other action ("Go to class")
   * lives in the modal the row opens.
   */
  const renderEntry = (event: ProgrammeCalendarEvent, isToday: boolean) => {
    const nowMs = now.getTime();
    const phase = isToday ? entryPhaseOf(event, nowMs) : 'later';
    const joinable = isToday && canJoin(event, nowMs);

    // "starts in N min" only inside the join window; otherwise no countdown.
    const status =
      phase === 'live'
        ? labels.happeningNow
        : phase === 'soon'
          ? labels.startsIn(
              Math.max(1, Math.round((new Date(event.startTime).getTime() - nowMs) / 60_000)),
            )
          : null;
    const host = event.hostName ? labels.entrySub(event.hostName) : null;
    const sub = host && status ? `${host} · ${status}` : (status ?? host);

    return (
      <div
        key={event.eventId}
        className={cn('z-entry', isToday && 'z-entry--today')}
        style={{ '--k-ink': event.kindLabelInk } as React.CSSProperties}
        onClick={(mouseEvent) => {
          mouseEvent.stopPropagation();
          setSelected(event);
        }}
      >
        <span className="z-time">{timeOf(event.startTime)}</span>
        <span className="z-entry-body">
          <span className="z-et">{isToday ? <mark>{event.title}</mark> : event.title}</span>
          <span className="z-ek">{event.kindLabel}</span>
          {sub && <span className={cn('z-sub', phase === 'live' && 'z-sub--live')}>{sub}</span>}
          {joinable && (
            <span className="z-entry-actions">
              <button
                type="button"
                className="z-join-btn"
                onClick={(mouseEvent) => {
                  mouseEvent.stopPropagation();
                  openJoinUrl(event.joinUrl!);
                }}
              >
                {labels.joinNow}
              </button>
            </span>
          )}
        </span>
      </div>
    );
  };

  const renderDay = (day: DayGroup) => {
    const isTodayDay = isSameDay(day.date, today);
    const isPast = !isTodayDay && day.date < today;
    const dayNo = zineZeroPad(day.date.getDate());
    const weekday = format(day.date, 'EEE', { locale });
    const setDayRef = (el: HTMLDivElement | null) => {
      if (el) dayRefs.current.set(day.key, el);
      else dayRefs.current.delete(day.key);
    };

    if (!isTodayDay) {
      return (
        <div
          key={day.key}
          ref={setDayRef}
          className={cn('z-day', isPast && 'z-day--past', flashKey === day.key && 'z-flash')}
        >
          <span className="z-dayno">
            {dayNo}
            <small>{weekday}</small>
          </span>
          <div>{day.events.map((event) => renderEntry(event, false))}</div>
        </div>
      );
    }

    // Today = the taped clip. Status lines and actions live on each entry
    // (see renderEntry), so every button names the event it belongs to.
    return (
      <div
        key={day.key}
        ref={setDayRef}
        className={cn('z-day', 'z-day--today', flashKey === day.key && 'z-flash')}
      >
        <span className="z-dayno">
          {dayNo}
          <small>{weekday}</small>
        </span>
        <div>
          <span className="z-today-flag">{labels.todayFlag}</span>
          {day.events.map((event) => renderEntry(event, true))}
        </div>
      </div>
    );
  };

  const renderMiniCell = (cell: MiniCell) => {
    if (!cell.inMonth) {
      return <div className="z-d out">{cell.date.getDate()}</div>;
    }
    const isTodayCell = isSameDay(cell.date, today);
    const multi = cell.dayKinds.length > 1;
    // Dot inks are data-driven: --k-dot colors the (first) dot, --k-dot2 the
    // stacked second dot of a multi-kind day. The today cell's own dot stays
    // white via the stylesheet, exactly as in the spec.
    const style = cell.hasEvents
      ? ({
          '--k-dot': cell.dayKinds[0].ink,
          ...(multi ? { '--k-dot2': cell.dayKinds[1].ink } : null),
        } as React.CSSProperties)
      : undefined;
    return (
      <div
        className={cn('z-d', isTodayCell && 'today', cell.hasEvents && 'dot', multi && 'multi')}
        style={style}
        onClick={cell.hasEvents ? () => jumpToDay(cell.key) : undefined}
      >
        {cell.date.getDate()}
      </div>
    );
  };

  return (
    <div className={cn(PROGRAMME_ROOT_CLASS, className)}>
      <ProgrammeCalendarStyles />

      {/* ═══ THE FULL MONTH ═══ */}
      <section className="z-spread" aria-label={labels.monthSectionAria}>
        <div className="z-page">
          <div className="z-spread-head">
            <span className="z-bigno">{zineZeroPad(monthStart.getMonth() + 1)}</span>
            <div>
              <p className="z-spread-eyebrow">{labels.eyebrow}</p>
              <h2>{labels.title}</h2>
            </div>
            <div className="z-monthbar">
              <button
                className="z-nav-btn"
                type="button"
                aria-label={labels.prevMonth}
                onClick={() => onMonthChange?.(startOfMonth(addMonths(monthStart, -1)))}
              >
                ‹
              </button>
              <span className="z-mlabel">{format(monthStart, 'MMMM yyyy', { locale })}</span>
              <button
                className="z-nav-btn"
                type="button"
                aria-label={labels.nextMonth}
                onClick={() => onMonthChange?.(startOfMonth(addMonths(monthStart, 1)))}
              >
                ›
              </button>
              <button className="z-nav-btn z-nav-btn--today" type="button" onClick={handleToday}>
                {labels.today}
              </button>
            </div>
          </div>

          <div className="z-cols">
            <div className="z-scroll" ref={scrollRef} onScroll={handleScroll}>
              {/* The sign container always renders (it is height 0) so week
                  spacing matches the spec whether or not the sign shows. */}
              <div className="z-scroll-sign">
                {signVisible && (
                  <button type="button" onClick={handleSignClick}>
                    {atToday ? labels.scrollUp(earlierWeeks) : labels.backToToday}
                  </button>
                )}
              </div>
              {weeks.map((week) => {
                const isCurrentWeek =
                  monthContainsToday && isSameDay(week.weekStart, startOfWeek(today, { locale }));
                const startLabel = format(week.labelStart, 'MMM d', { locale });
                const endLabel = isSameMonth(week.labelEnd, week.labelStart)
                  ? format(week.labelEnd, 'd', { locale })
                  : format(week.labelEnd, 'MMM d', { locale });
                const range = `${startLabel} – ${endLabel}`;
                return (
                  <div className="z-wk" key={week.key}>
                    <p className="z-wk-label">
                      {isCurrentWeek ? (
                        <>
                          <span className="z-now">{labels.thisWeek}</span>
                          {` · ${range}`}
                        </>
                      ) : (
                        range
                      )}
                    </p>
                    {week.days.map(renderDay)}
                  </div>
                );
              })}
            </div>

            <aside className="z-aside">
              <div className="z-clip">
                {/* <h3>{labels.atAGlance(format(monthStart, 'MMMM', { locale }))}</h3> */}
                <table className="z-mini">
                  <thead>
                    <tr>
                      {miniWeekdays.map((weekday, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <th key={index}>{weekday}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {miniWeeks.map((row) => (
                      <tr key={row[0].key}>
                        {row.map((cell) => (
                          <td key={cell.key}>{renderMiniCell(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="z-workbook">
                <span className="z-wb-tab">{labels.legendTitle}</span>
                {legendRows.map((row) => (
                  <div className="z-legend-row" key={row.kind}>
                    <i style={{ background: row.ink }} />
                    <span>{row.label}</span>
                  </div>
                ))}
                {labels.legendNote ? <p className="z-wb-note">{labels.legendNote}</p> : null}
              </div>

              {resourceLinks && resourceLinks.length > 0 && (
                <div className="z-workbook z-resources">
                  {labels.resourcesTitle ? (
                    <span className="z-wb-tab">{labels.resourcesTitle}</span>
                  ) : null}
                  {resourceLinks.map((link) => (
                    <a
                      key={link.href}
                      className="z-res-btn"
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* ═══ INTERLUDE ═══ */}
      {interlude && (
        <section className="z-interlude" aria-label={labels.quoteAria}>
          <div className="z-page">
            <p className="z-q">{interlude.quote}</p>
            <p className="z-who">{interlude.attribution}</p>
          </div>
        </section>
      )}

      {/* ═══ EVENT DETAIL (opens when an event in the list is clicked) ═══ */}
      {selected && (
        <div
          className="z-modal-wrap open"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
          onClick={(mouseEvent) => {
            if (mouseEvent.target === mouseEvent.currentTarget) closeModal();
          }}
        >
          <div className="z-modal">
            <button
              className="z-m-close"
              type="button"
              aria-label={labels.close}
              onClick={closeModal}
            >
              ✕
            </button>
            <p
              className="z-m-kind"
              style={{ '--k-ink': selected.kindLabelInk } as React.CSSProperties}
            >
              {selected.kindLabel}
            </p>
            <h3 id={modalTitleId}>{selected.title}</h3>
            <p className="z-m-when">
              {`${format(new Date(selected.startTime), 'EEE, MMMM d', { locale })} · ${timeOf(selected.startTime)}`}
            </p>
            {selected.hostName && (
              <div className="z-m-row">
                <span className="z-m-label">{labels.with}</span>
                <span className="z-m-avatar">
                  {selected.hostAvatarUrl ? (
                    <img
                      src={selected.hostAvatarUrl}
                      alt=""
                      aria-hidden="true"
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    selected.hostName.trim().charAt(0).toUpperCase()
                  )}
                </span>
                <span>{selected.hostName}</span>
              </div>
            )}
            {selected.locationText && (
              <div className="z-m-row">
                <span className="z-m-label">{labels.where}</span>
                <span>{selected.locationText}</span>
              </div>
            )}
            {(() => {
              // Same rule as the listing: Join only inside its window. Outside
              // it the meeting link is withheld, with a note saying when it opens.
              const joinable = canJoin(selected, now.getTime());
              const pending =
                Boolean(selected.joinUrl) &&
                !joinable &&
                entryPhaseOf(selected, now.getTime()) === 'later';
              const showClass = Boolean(onGoToClass && selected.classId);
              return (
                <>
                  {(joinable || showClass) && (
                    <div className="z-m-actions">
                      {joinable && (
                        <button
                          className="z-join-btn"
                          type="button"
                          onClick={() => openJoinUrl(selected.joinUrl!)}
                        >
                          {labels.join}
                        </button>
                      )}
                      {showClass && (
                        <button
                          className="z-btn-dark"
                          type="button"
                          onClick={() => onGoToClass!(selected)}
                        >
                          {labels.goToClass}
                        </button>
                      )}
                    </div>
                  )}
                  {pending && labels.joinWindowNote ? (
                    <p className="z-m-note">{labels.joinWindowNote}</p>
                  ) : null}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
